import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import {
    Badge,
    Collapse,
    IconButton,
    List,
    Menu,
    MenuItem,
    useTheme,
} from "@material-ui/core";

import Dashboard from "@material-ui/icons/Dashboard";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import Contacts from "@material-ui/icons/Contacts";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import Help from "@material-ui/icons/Help";
import DeveloperModeIcon from "@material-ui/icons/DeveloperMode";
import Schedule from "@material-ui/icons/Schedule";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import PeopleIcon from "@material-ui/icons/People";
import ListIcon from "@material-ui/icons/ListAlt";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import ForumIcon from "@material-ui/icons/Forum";
import AttachFile from "@material-ui/icons/AttachFile";
import DeviceHubOutlined from "@material-ui/icons/DeviceHubOutlined";
import EmojiObjectsIcon from "@material-ui/icons/EmojiObjects";
import {
    AccountCircle,
    Brightness3,
    Brightness7,
    ExitToApp,
    FormatListBulletedSharp,
    LocalAtm,
    PeopleAltOutlined,
    Style,
    VolumeDown,
    VolumeUp,
} from "@material-ui/icons";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import { socketConnection } from "../services/socket";
import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import usePlans from "../hooks/usePlans";

import ColorModeContext from "../layout/themeContext";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";

const useStyles = makeStyles((theme) => ({
    // Item selecionado — usa palette.type (correto no MUI v4)
    selected: {
        backgroundColor: theme.palette.type === "dark" ? "#333" : "#e4e8f4",
        "&:hover": {
            backgroundColor: theme.palette.type === "dark" ? "#333" : "#e4e8f4",
        },
        borderLeft: theme.palette.type === "dark" ? "3px solid #00BFFF" : `3px solid ${theme.palette.primary.main}`,
    },

    // Container externo (preenche a altura do drawer)
    footerTop: (props) => ({
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingRight: props.collapsed ? "11px" : "16px",
        paddingLeft: props.collapsed ? "11px" : "16px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        // animação removida — disparava a cada re-render
    }),

    // Caixa interna com scroll e rodapé fixo
    footerBottom: {
        backgroundColor: theme.palette.type === "dark" ? "#2f3646" : "#f3f3f3",
        overflow: "hidden",
        clipPath: "inset(0px round 8px)",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
    },

    // Lista scrollável — ocupa o espaço disponível via flex
    scrollableList: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        "&:hover": {
            "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.grey[500],
            },
        },
        "&::-webkit-scrollbar": {
            width: "4px",
        },
        "&::-webkit-scrollbar-track": {
            background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "transparent",
            borderRadius: "8px",
        },
    },

    // Rodapé fixo (conta + sair)
    bottomList: {
        flexShrink: 0,
        height: "auto",
    },

    listItemText: {
        fontSize: "0.8125rem",
    },

    nested: {
        paddingLeft: theme.spacing(4),
    },

    // Rótulo de seção (visível só quando expandido)
    sectionLabel: {
        padding: "10px 16px 2px",
        display: "block",
        color: theme.palette.text.disabled,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        fontSize: "0.62rem",
    },

    // Informações do usuário no rodapé
    userAvatar: {
        width: 32,
        height: 32,
        fontSize: "0.85rem",
        backgroundColor: theme.palette.primary.main,
    },
    userName: {
        fontWeight: 600,
        fontSize: "0.85rem",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    userEmail: {
        fontSize: "0.7rem",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: theme.palette.text.secondary,
    },
}));

// ---------------------------------------------------------------------------
// ListItemLink — item de navegação com tooltip quando colapsado
// ---------------------------------------------------------------------------
function ListItemLink({ icon, primary, to, className, selected, onClick, collapsed }) {
    const classes = useStyles();

    const renderLink = React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
    ));

    return (
        <li>
            {/* title="" desativa o tooltip quando expandido */}
            <Tooltip title={collapsed ? primary : ""} placement="right" arrow>
                <ListItem
                    button
                    dense
                    component={renderLink}
                    onClick={onClick}
                    className={`${className || ""} ${selected ? classes.selected : ""}`}
                    selected={selected}
                >
                    {icon && <ListItemIcon>{icon}</ListItemIcon>}
                    <ListItemText
                        primary={primary}
                        classes={{ primary: classes.listItemText }}
                    />
                </ListItem>
            </Tooltip>
        </li>
    );
}

// ---------------------------------------------------------------------------
// SectionLabel — rótulo de grupo (oculto quando colapsado)
// ---------------------------------------------------------------------------
function SectionLabel({ children, collapsed, classes }) {
    if (collapsed) return <Divider style={{ margin: "4px 0" }} />;
    return (
        <li style={{ listStyle: "none" }}>
            <Typography variant="caption" className={classes.sectionLabel}>
                {children}
            </Typography>
        </li>
    );
}

// ---------------------------------------------------------------------------
// Reducer de chats internos
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
    if (action.type === "LOAD_CHATS") {
        const chats = action.payload;
        const newChats = [];
        if (isArray(chats)) {
            chats.forEach((chat) => {
                const idx = state.findIndex((u) => u.id === chat.id);
                if (idx !== -1) state[idx] = chat;
                else newChats.push(chat);
            });
        }
        return [...state, ...newChats];
    }
    if (action.type === "UPDATE_CHATS") {
        const chat = action.payload;
        const idx = state.findIndex((u) => u.id === chat.id);
        if (idx !== -1) { state[idx] = chat; return [...state]; }
        return [chat, ...state];
    }
    if (action.type === "DELETE_CHAT") {
        const idx = state.findIndex((u) => u.id === action.payload);
        if (idx !== -1) state.splice(idx, 1);
        return [...state];
    }
    if (action.type === "RESET") return [];
    if (action.type === "CHANGE_CHAT") {
        return state.map((c) =>
            c.id === action.payload.chat.id ? action.payload.chat : c
        );
    }
    return state;
};

// ---------------------------------------------------------------------------
// MainListItems
// ---------------------------------------------------------------------------
const MainListItems = (props) => {
    const { drawerClose, collapsed, volume, setVolume } = props;
    const classes = useStyles(props);
    const { whatsApps } = useContext(WhatsAppsContext);
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [anchorElAccount, setAnchorElAccount] = useState(null);
    const { user, handleLogout } = useContext(AuthContext);
    const { colorMode } = useContext(ColorModeContext);
    const [connectionWarning, setConnectionWarning] = useState(false);
    const [showCampaigns, setShowCampaigns] = useState(false);
    const [showKanban, setShowKanban] = useState(false);
    const [showOpenAi, setShowOpenAi] = useState(false);
    const [showIntegrations, setShowIntegrations] = useState(false);
    const [showSchedules, setShowSchedules] = useState(false);
    const [showInternalChat, setShowInternalChat] = useState(false);
    const [showExternalApi, setShowExternalApi] = useState(false);
    const [invisible, setInvisible] = useState(true);
    const [pageNumber] = useState(1);
    const [searchParam] = useState("");
    const [chats, dispatch] = useReducer(reducer, []);
    const { getPlanCompany } = usePlans();
    const location = useLocation();
    const theme = useTheme();

    // Planos e feature flags
    useEffect(() => {
        async function fetchData() {
            const planConfigs = await getPlanCompany(undefined, user.companyId);
            setShowCampaigns(planConfigs.plan.useCampaigns);
            setShowKanban(planConfigs.plan.useKanban);
            setShowOpenAi(planConfigs.plan.useOpenAi);
            setShowIntegrations(planConfigs.plan.useIntegrations);
            setShowSchedules(planConfigs.plan.useSchedules);
            setShowInternalChat(planConfigs.plan.useInternalChat);
            setShowExternalApi(planConfigs.plan.useExternalApi);
        }
        fetchData();
    }, [user.companyId, getPlanCompany]);

    // Chats internos — busca e socket
    useEffect(() => {
        const t = setTimeout(() => fetchChats(), 500);
        return () => clearTimeout(t);
    }, [searchParam, pageNumber]); // eslint-disable-line

    useEffect(() => {
        const companyId = localStorage.getItem("companyId");
        const socket = socketConnection({ companyId });
        socket.on(`company-${companyId}-chat`, (data) => {
            if (data.action === "new-message" || data.action === "update") {
                dispatch({ type: "CHANGE_CHAT", payload: data });
            }
        });
        return () => socket.disconnect();
    }, []);

    // Badge de mensagens não lidas
    useEffect(() => {
        let count = 0;
        if (chats.length > 0) {
            chats.forEach((chat) => {
                chat.users.forEach((cu) => {
                    if (cu.userId === user.id) count += cu.unreads;
                });
            });
        }
        setInvisible(count <= 0);
    }, [chats, user.id]);

    // Override local para exibir campanhas
    useEffect(() => {
        if (localStorage.getItem("cshow")) setShowCampaigns(true);
    }, []);

    // Status das conexões WhatsApp
    useEffect(() => {
        const t = setTimeout(() => {
            if (whatsApps.length > 0) {
                const offline = whatsApps.filter((w) =>
                    ["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(w.status)
                );
                setConnectionWarning(offline.length > 0);
            }
        }, 2000);
        return () => clearTimeout(t);
    }, [whatsApps]);

    const fetchChats = async () => {
        try {
            const { data } = await api.get("/chats/", {
                params: { searchParam, pageNumber },
            });
            dispatch({ type: "LOAD_CHATS", payload: data.records });
        } catch (err) {
            toastError(err);
        }
    };

    const handleClickLogout = () => handleLogout();

    const handleOpenUserModal = () => {
        setUserModalOpen(true);
        setAnchorElAccount(null);
    };

    const toggleColorMode = () => colorMode.toggleColorMode();

    // Inicial do nome do usuário para o avatar
    const userInitial = user?.name ? user.name.charAt(0).toUpperCase() : "?";

    return (
        <div className={classes.footerTop}>
            <UserModal
                open={userModalOpen}
                onClose={() => setUserModalOpen(false)}
                userId={user?.id}
            />

            <div className={classes.footerBottom}>
                {/* Lista principal scrollável */}
                <List className={classes.scrollableList} disablePadding>

                    {/* Dashboard — só admins */}
                    <Can
                        role={user.profile}
                        perform="drawer-admin-items:view"
                        yes={() => (
                            <>
                                <SectionLabel collapsed={collapsed} classes={classes}>
                                    Visão Geral
                                </SectionLabel>
                                <ListItemLink
                                    to="/"
                                    primary="Dashboard"
                                    icon={<Dashboard />}
                                    selected={location.pathname === "/"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                <Divider />
                            </>
                        )}
                    />

                    {/* Itens de atendimento — usuários comuns (não admins) */}
                    <Can
                        role={user.profile}
                        perform="drawer-admin-items:view"
                        no={() => (
                            <>
                                <SectionLabel collapsed={collapsed} classes={classes}>
                                    Atendimento
                                </SectionLabel>
                                <ListItemLink
                                    to="/tickets"
                                    primary={i18n.t("mainDrawer.listItems.tickets")}
                                    icon={<WhatsAppIcon />}
                                    selected={location.pathname.startsWith("/tickets")}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                <ListItemLink
                                    to="/quick-messages"
                                    primary={i18n.t("mainDrawer.listItems.quickMessages")}
                                    icon={<FlashOnIcon />}
                                    selected={location.pathname === "/quick-messages"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                {showKanban && (
                                    <ListItemLink
                                        to="/kanban"
                                        primary="Kanban"
                                        icon={<Style />}
                                        selected={location.pathname === "/kanban"}
                                        onClick={drawerClose}
                                        collapsed={collapsed}
                                    />
                                )}
                                <ListItemLink
                                    to="/todolist"
                                    primary={i18n.t("Tarefas")}
                                    icon={<FormatListBulletedSharp />}
                                    selected={location.pathname === "/todolist"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                <ListItemLink
                                    to="/contacts"
                                    primary={i18n.t("mainDrawer.listItems.contacts")}
                                    icon={<Contacts />}
                                    selected={location.pathname === "/contacts"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                {showSchedules && (
                                    <ListItemLink
                                        to="/schedules"
                                        primary={i18n.t("mainDrawer.listItems.schedules")}
                                        icon={<Schedule />}
                                        selected={location.pathname === "/schedules"}
                                        onClick={drawerClose}
                                        collapsed={collapsed}
                                    />
                                )}
                                <ListItemLink
                                    to="/tags"
                                    primary={i18n.t("mainDrawer.listItems.tags")}
                                    icon={<LocalOfferIcon />}
                                    selected={location.pathname === "/tags"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                {showInternalChat && (
                                    <ListItemLink
                                        to="/chats"
                                        primary={i18n.t("mainDrawer.listItems.chats")}
                                        icon={
                                            <Badge color="secondary" variant="dot" invisible={invisible}>
                                                <ForumIcon />
                                            </Badge>
                                        }
                                        selected={location.pathname === "/chats"}
                                        onClick={drawerClose}
                                        collapsed={collapsed}
                                    />
                                )}
                                <ListItemLink
                                    to="/helps"
                                    primary={i18n.t("mainDrawer.listItems.helps")}
                                    icon={<Help />}
                                    selected={location.pathname === "/helps"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                            </>
                        )}
                    />

                    {/* Itens administrativos */}
                    <Can
                        role={user.profile}
                        perform="drawer-admin-items:view"
                        yes={() => (
                            <>
                                <SectionLabel collapsed={collapsed} classes={classes}>
                                    Atendimento
                                </SectionLabel>
                                <ListItemLink
                                    to="/tickets"
                                    primary={i18n.t("mainDrawer.listItems.tickets")}
                                    icon={<WhatsAppIcon />}
                                    selected={location.pathname.startsWith("/tickets")}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                <ListItemLink
                                    to="/contacts"
                                    primary={i18n.t("mainDrawer.listItems.contacts")}
                                    icon={<Contacts />}
                                    selected={location.pathname === "/contacts"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                {showSchedules && (
                                    <ListItemLink
                                        to="/schedules"
                                        primary={i18n.t("mainDrawer.listItems.schedules")}
                                        icon={<Schedule />}
                                        selected={location.pathname === "/schedules"}
                                        onClick={drawerClose}
                                        collapsed={collapsed}
                                    />
                                )}
                                {showKanban && (
                                    <ListItemLink
                                        to="/kanban"
                                        primary="Kanban"
                                        icon={<Style />}
                                        selected={location.pathname === "/kanban"}
                                        onClick={drawerClose}
                                        collapsed={collapsed}
                                    />
                                )}
                                {showInternalChat && (
                                    <ListItemLink
                                        to="/chats"
                                        primary={i18n.t("mainDrawer.listItems.chats")}
                                        icon={
                                            <Badge color="secondary" variant="dot" invisible={invisible}>
                                                <ForumIcon />
                                            </Badge>
                                        }
                                        selected={location.pathname === "/chats"}
                                        onClick={drawerClose}
                                        collapsed={collapsed}
                                    />
                                )}
                                <ListItemLink
                                    to="/todolist"
                                    primary={i18n.t("Tarefas")}
                                    icon={<FormatListBulletedSharp />}
                                    selected={location.pathname === "/todolist"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />

                                <Divider style={{ margin: "4px 0" }} />
                                <SectionLabel collapsed={collapsed} classes={classes}>
                                    Administração
                                </SectionLabel>

                                <CampaignSubmenu
                                    showCampaigns={showCampaigns}
                                    location={location}
                                    drawerClose={drawerClose}
                                    collapsed={collapsed}
                                />
                                <ListItemLink
                                    to="/connections"
                                    primary={i18n.t("mainDrawer.listItems.connections")}
                                    icon={
                                        <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
                                            <SyncAltIcon />
                                        </Badge>
                                    }
                                    selected={location.pathname === "/connections"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                <ListItemLink
                                    to="/queues"
                                    primary={i18n.t("mainDrawer.listItems.queues")}
                                    icon={<AccountTreeOutlinedIcon />}
                                    selected={location.pathname === "/queues"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                <ListItemLink
                                    to="/users"
                                    primary={i18n.t("mainDrawer.listItems.users")}
                                    icon={<PeopleAltOutlined />}
                                    selected={location.pathname === "/users"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                <ListItemLink
                                    to="/tags"
                                    primary={i18n.t("mainDrawer.listItems.tags")}
                                    icon={<LocalOfferIcon />}
                                    selected={location.pathname === "/tags"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                <ListItemLink
                                    to="/quick-messages"
                                    primary={i18n.t("mainDrawer.listItems.quickMessages")}
                                    icon={<FlashOnIcon />}
                                    selected={location.pathname === "/quick-messages"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                <ListItemLink
                                    to="/files"
                                    primary={i18n.t("mainDrawer.listItems.files")}
                                    icon={<AttachFile />}
                                    selected={location.pathname === "/files"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                {showOpenAi && (
                                    <ListItemLink
                                        to="/prompts"
                                        primary={i18n.t("mainDrawer.listItems.prompts")}
                                        icon={<EmojiObjectsIcon />}
                                        selected={location.pathname === "/prompts"}
                                        onClick={drawerClose}
                                        collapsed={collapsed}
                                    />
                                )}
                                {showIntegrations && (
                                    <ListItemLink
                                        to="/queue-integration"
                                        primary={i18n.t("mainDrawer.listItems.queueIntegration")}
                                        icon={<DeviceHubOutlined />}
                                        selected={location.pathname === "/queue-integration"}
                                        onClick={drawerClose}
                                        collapsed={collapsed}
                                    />
                                )}
                                {showExternalApi && (
                                    <ListItemLink
                                        to="/messages-api"
                                        primary={i18n.t("mainDrawer.listItems.messagesAPI")}
                                        icon={<DeveloperModeIcon />}
                                        selected={location.pathname === "/messages-api"}
                                        onClick={drawerClose}
                                        collapsed={collapsed}
                                    />
                                )}
                                <ListItemLink
                                    to="/financeiro"
                                    primary={i18n.t("mainDrawer.listItems.financeiro")}
                                    icon={<LocalAtm />}
                                    selected={location.pathname === "/financeiro"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                <ListItemLink
                                    to="/settings"
                                    primary={i18n.t("mainDrawer.listItems.settings")}
                                    icon={<SettingsOutlinedIcon />}
                                    selected={location.pathname === "/settings"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                {user.super && (
                                    <ListItemLink
                                        to="/announcements"
                                        primary={i18n.t("mainDrawer.listItems.annoucements")}
                                        icon={<AnnouncementIcon />}
                                        selected={location.pathname === "/announcements"}
                                        onClick={drawerClose}
                                        collapsed={collapsed}
                                    />
                                )}
                                <ListItemLink
                                    to="/helps"
                                    primary={i18n.t("mainDrawer.listItems.helps")}
                                    icon={<Help />}
                                    selected={location.pathname === "/helps"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                            </>
                        )}
                    />
                </List>

                <Divider />

                {/* Rodapé fixo: conta do usuário + sair */}
                <List className={classes.bottomList} disablePadding>
                    <Tooltip title={collapsed ? "Minha conta" : ""} placement="right" arrow>
                        <ListItem button onClick={(e) => setAnchorElAccount(e.currentTarget)}>
                            <ListItemAvatar style={{ minWidth: collapsed ? 40 : 56 }}>
                                <Avatar className={classes.userAvatar}>{userInitial}</Avatar>
                            </ListItemAvatar>
                            {!collapsed && (
                                <ListItemText
                                    primary={
                                        <Typography className={classes.userName} noWrap>
                                            {user?.name || "Minha conta"}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography className={classes.userEmail} noWrap>
                                            {user?.email || ""}
                                        </Typography>
                                    }
                                    disableTypography
                                />
                            )}
                        </ListItem>
                    </Tooltip>

                    <Tooltip title={collapsed ? "Sair" : ""} placement="right" arrow>
                        <ListItem button dense onClick={handleClickLogout}>
                            <ListItemIcon>
                                <ExitToApp />
                            </ListItemIcon>
                            <ListItemText
                                primary="Sair"
                                classes={{ primary: classes.listItemText }}
                            />
                        </ListItem>
                    </Tooltip>
                </List>
            </div>

            {/* Menu de contexto da conta */}
            <Menu
                id="account-menu"
                anchorEl={anchorElAccount}
                open={Boolean(anchorElAccount)}
                onClose={() => setAnchorElAccount(null)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ style: { maxHeight: 320, width: 230 } }}
            >
                <MenuItem
                    onClick={() => {
                        handleOpenUserModal();
                        drawerClose();
                    }}
                >
                    <ListItemIcon><PeopleAltOutlined /></ListItemIcon>
                    <ListItemText primary="Perfil" />
                </MenuItem>

                <MenuItem onClick={toggleColorMode}>
                    <ListItemIcon>
                        {theme.palette.type === "dark" ? <Brightness3 /> : <Brightness7 />}
                    </ListItemIcon>
                    <ListItemText primary="Modo Escuro" />
                </MenuItem>

                <MenuItem
                    style={{ cursor: "default" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div style={{ width: "100%", display: "flex", flexDirection: "column", padding: "4px 0" }}>
                        <ListItemText primary="Volume das Notificações" style={{ marginBottom: 4 }} />
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <VolumeDown style={{ marginRight: 8, color: theme.palette.text.secondary }} />
                            <NotificationsVolume volume={volume} onChange={setVolume} />
                            <VolumeUp style={{ marginLeft: 8, color: theme.palette.text.secondary }} />
                        </div>
                    </div>
                </MenuItem>
            </Menu>
        </div>
    );
};

// ---------------------------------------------------------------------------
// CampaignSubmenu
// ---------------------------------------------------------------------------
function CampaignSubmenu({ showCampaigns, location, drawerClose, collapsed }) {
    const [open, setOpen] = useState(false);
    const classes = useStyles();

    if (!showCampaigns) return null;

    const handleToggle = (e) => {
        e.stopPropagation();
        setOpen((prev) => !prev);
    };

    return (
        <>
            <Tooltip title={collapsed ? i18n.t("mainDrawer.listItems.campaigns") : ""} placement="right" arrow>
                <ListItem button onClick={handleToggle}>
                    <ListItemIcon>
                        <EventAvailableIcon />
                    </ListItemIcon>
                    <ListItemText primary={i18n.t("mainDrawer.listItems.campaigns")} />
                    {!collapsed && (open ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                </ListItem>
            </Tooltip>
            <Collapse in={open && !collapsed} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    <ListItemLink
                        to="/campaigns"
                        primary="Listagem"
                        icon={<ListIcon />}
                        selected={location.pathname === "/campaigns"}
                        onClick={drawerClose}
                        className={classes.nested}
                        collapsed={false}
                    />
                    <ListItemLink
                        to="/contact-lists"
                        primary="Listas de Contatos"
                        icon={<PeopleIcon />}
                        selected={location.pathname === "/contact-lists"}
                        onClick={drawerClose}
                        className={classes.nested}
                        collapsed={false}
                    />
                    <ListItemLink
                        to="/campaigns-config"
                        primary="Configurações"
                        icon={<SettingsOutlinedIcon />}
                        selected={location.pathname === "/campaigns-config"}
                        onClick={drawerClose}
                        className={classes.nested}
                        collapsed={false}
                    />
                </List>
            </Collapse>
        </>
    );
}

export default MainListItems;
